import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
} from '@mui/material';
import {
  TrendingUp,
  ShoppingCart,
  AttachMoney,
  Receipt,
  Refresh,
  ArrowBack,
  EmojiEvents,
  CalendarToday,
} from '@mui/icons-material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import { useAuth } from '../../hooks/useAuth.js';
import { OrderService } from '../../services/orderService.js';
import { LedgerService } from '../../services/ledgerService.js';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// ─── Helpers ────────────────────────────────────────────────────────────────

const fmt = (n) =>
  n >= 100000
    ? `₹${(n / 100000).toFixed(1)}L`
    : n >= 1000
      ? `₹${(n / 1000).toFixed(1)}K`
      : `₹${n.toFixed(0)}`;

/** Show short date like "9 Mar" */
const shortDate = (dateStr) => {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

/** Smart label: weekday for ≤ 7 days, short date for longer ranges */
const smartLabel = (dateStr, totalDays) => {
  const d = new Date(dateStr);
  if (totalDays <= 7) {
    return d.toLocaleDateString('en-IN', { weekday: 'short' });
  }
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

const formatFullDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/** Format a Date to YYYY-MM-DD for <input type="date"> */
const toISODate = (d) => d.toISOString().split('T')[0];

/** Build default "last N days" range */
const buildRange = (days) => {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - (days - 1));
  start.setHours(0, 0, 0, 0);
  return { start, end };
};

// ─── Date-range presets ──────────────────────────────────────────────────────
const PRESETS = [
  { key: '7d', label: '7 D', days: 7 },
  { key: '14d', label: '14 D', days: 14 },
  { key: '30d', label: '30 D', days: 30 },
  { key: 'month', label: 'This Month', days: null },
];

const getPresetRange = (preset) => {
  if (preset.key === 'month') {
    const now = new Date();
    return {
      start: new Date(now.getFullYear(), now.getMonth(), 1),
      end: now,
    };
  }
  return buildRange(preset.days);
};

// ─── KPI Card ───────────────────────────────────────────────────────────────

const KpiCard = ({ icon, label, value, sub, color, bgGrad }) => (
  <Box
    sx={{
      flex: 1,
      minWidth: 180,
      borderRadius: '18px',
      p: 2.5,
      background: bgGrad,
      color: 'rgba(255, 255, 255, 0.72)',
      display: 'flex',
      flexDirection: 'column',
      gap: 1,
      position: 'relative',
      overflow: 'hidden',
      boxShadow: `0 4px 20px ${color}33`,
    }}
  >
    <Box
      sx={{
        position: 'absolute',
        top: -12,
        right: -12,
        width: 72,
        height: 72,
        borderRadius: '50%',
        bgcolor: 'rgba(255,255,255,0.12)',
      }}
    />
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {icon}
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, opacity: 0.85, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
        {label}
      </Typography>
    </Box>
    <Typography sx={{ fontSize: '1.65rem', fontWeight: 800, lineHeight: 1.1, letterSpacing: '-0.5px' }}>
      {value}
    </Typography>
    {sub && (
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 500, opacity: 0.7 }}>
        {sub}
      </Typography>
    )}
  </Box>
);

// ─── Section Wrapper ────────────────────────────────────────────────────────

const Section = ({ title, children, sx }) => (
  <Box
    sx={{
      bgcolor: 'rgba(255, 255, 255, 0.72)',
      borderRadius: '18px',

      overflow: 'hidden',
      ...sx,
    }}
  >
    {title && (
      <Box sx={{ px: 2.5, pt: 2, pb: 1 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.92rem', color: 'text.primary' }}>
          {title}
        </Typography>
      </Box>
    )}
    {children}
  </Box>
);

// ─── Main Dashboard ─────────────────────────────────────────────────────────

export const DashboardPanel = ({ onBack }) => {
  const { outletId } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // ── Date range state (default: last 7 days) ───────────────────────────
  const defaultRange = buildRange(7);
  const [activePreset, setActivePreset] = useState('7d');
  const [rangeStart, setRangeStart] = useState(defaultRange.start);
  const [rangeEnd, setRangeEnd] = useState(defaultRange.end);

  // ── Data state ─────────────────────────────────────────────────────────
  const [rangeStats, setRangeStats] = useState(null);
  const [salesTrend, setSalesTrend] = useState(null);
  const [topItems, setTopItems] = useState([]);
  const [allCompletedOrders, setAllCompletedOrders] = useState([]);
  const [activeOrders, setActiveOrders] = useState([]);
  const [ledgerEntries, setLedgerEntries] = useState([]);

  // ── Computed date boundaries for API calls ─────────────────────────────
  const apiStartDate = useMemo(() => {
    const d = new Date(rangeStart);
    d.setHours(0, 0, 0, 0);
    return d;
  }, [rangeStart]);

  const apiEndDate = useMemo(() => {
    // End date is exclusive for the API, so add 1 day
    const d = new Date(rangeEnd);
    d.setDate(d.getDate() + 1);
    d.setHours(0, 0, 0, 0);
    return d;
  }, [rangeEnd]);

  const totalDays = useMemo(() => {
    return Math.max(1, Math.ceil(
      (apiEndDate.getTime() - apiStartDate.getTime()) / (1000 * 60 * 60 * 24)
    ));
  }, [apiStartDate, apiEndDate]);

  // ── Range label for display ────────────────────────────────────────────
  const rangeLabel = useMemo(() => {
    const s = shortDate(rangeStart);
    const e = shortDate(rangeEnd);
    return `${s} – ${e}`;
  }, [rangeStart, rangeEnd]);

  // ── Fetch all data ─────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    if (!outletId) return;

    try {
      const [
        statsResult,
        trendResult,
        topItemsResult,
        completedResult,
        activeResult,
        ledgerResult,
      ] = await Promise.allSettled([
        LedgerService.getLedgerStatistics(outletId, apiStartDate, apiEndDate),
        LedgerService.getSalesTrendData(outletId, apiStartDate, rangeEnd),
        OrderService.fetchTopFoodItems(outletId),
        OrderService.fetchCompleteOrders(outletId),
        OrderService.fetchOrders(outletId, 'Active'),
        LedgerService.fetchLedgerForDateRange(outletId, apiStartDate, apiEndDate),
      ]);

      if (statsResult.status === 'fulfilled') setRangeStats(statsResult.value);
      if (trendResult.status === 'fulfilled') setSalesTrend(trendResult.value);
      if (topItemsResult.status === 'fulfilled') setTopItems(topItemsResult.value || []);
      if (completedResult.status === 'fulfilled') setAllCompletedOrders(completedResult.value || []);
      if (activeResult.status === 'fulfilled') setActiveOrders(activeResult.value || []);
      if (ledgerResult.status === 'fulfilled') setLedgerEntries(ledgerResult.value || []);
    } catch (err) {
      console.error('[Dashboard] Error loading data:', err);
    }
  }, [outletId, apiStartDate, apiEndDate, rangeEnd]);

  // Initial load
  useEffect(() => {
    (async () => {
      setLoading(true);
      await fetchData();
      setLoading(false);
    })();
  }, [fetchData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
  };

  // ── Preset handler ─────────────────────────────────────────────────────
  const handlePreset = (preset) => {
    const { start, end } = getPresetRange(preset);
    setActivePreset(preset.key);
    setRangeStart(start);
    setRangeEnd(end);
  };

  // ── Custom date handler ────────────────────────────────────────────────
  const handleCustomStart = (e) => {
    const val = e.target.value; // "YYYY-MM-DD"
    if (!val) return;
    setActivePreset(null);
    setRangeStart(new Date(val + 'T00:00:00'));
  };
  const handleCustomEnd = (e) => {
    const val = e.target.value;
    if (!val) return;
    setActivePreset(null);
    setRangeEnd(new Date(val + 'T23:59:59'));
  };

  // ── Derived values ─────────────────────────────────────────────────────
  const totalRevenue = rangeStats?.totalSales || 0;

  // Filter completed orders to only those within the selected date range
  const completedOrders = useMemo(() => {
    const startTs = apiStartDate.getTime();
    const endTs = apiEndDate.getTime();
    return allCompletedOrders.filter((order) => {
      if (!order.CreatedAt) return false;
      const ts = new Date(order.CreatedAt).getTime();
      return ts >= startTs && ts < endTs;
    });
  }, [allCompletedOrders, apiStartDate, apiEndDate]);

  const totalOrders = completedOrders.length;
  const avgOrder = totalOrders > 0 ? totalRevenue / totalOrders : 0;
  const activeCount = activeOrders.length;

  // ── Chart: Sales Trend ─────────────────────────────────────────────────
  const salesChartData = useMemo(() => {
    if (!salesTrend || !salesTrend.labels || salesTrend.labels.length === 0) return null;
    const hasData = salesTrend.salesData.some((v) => v > 0);
    if (!hasData) return null;
    return {
      labels: salesTrend.labels.map((l) => smartLabel(l, totalDays)),
      datasets: [
        {
          label: 'Revenue',
          data: salesTrend.salesData,
          bordercolor: 'primary.main',
          backgroundColor: 'rgba(69, 39, 160, 0.08)',
          fill: true,
          tension: 0.4,
          pointBackgroundcolor: 'primary.main',
          pointBorderColor: 'rgba(255, 255, 255, 0.72)',
          pointBorderWidth: 2,
          pointRadius: totalDays > 14 ? 3 : 5,
          pointHoverRadius: 7,
        },
      ],
    };
  }, [salesTrend, totalDays]);

  const salesChartOpts = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundcolor: 'text.primary',
          titleFont: { size: 11, weight: '600' },
          bodyFont: { size: 12, weight: '700' },
          padding: 10,
          cornerRadius: 8,
          callbacks: {
            label: (ctx) => `₹${ctx.parsed.y.toLocaleString('en-IN')}`,
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: {
            font: { size: 10, weight: '600' },
            color: 'text.disabled',
            maxRotation: totalDays > 14 ? 45 : 0,
          },
        },
        y: {
          grid: { color: 'rgba(0, 0, 0, 0.03)' },
          ticks: {
            font: { size: 10 },
            color: 'text.disabled',
            callback: (v) => (v >= 1000 ? `${v / 1000}K` : v),
          },
          beginAtZero: true,
        },
      },
    }),
    [totalDays]
  );

  // ── Chart: Top Items Bar ───────────────────────────────────────────────
  const topItemsChartData = useMemo(() => {
    const items = topItems.slice(0, 6);
    return {
      labels: items.map((i) =>
        (i.item_name || '').length > 14
          ? (i.item_name || '').slice(0, 14) + '…'
          : i.item_name || ''
      ),
      datasets: [
        {
          label: 'Qty Sold',
          data: items.map((i) => i.total_quantity || 0),
          backgroundColor: [
            '#4527A0',
            '#7E57C2',
            '#9575CD',
            '#B39DDB',
            'rgba(69, 39, 160, 0.15)',
            'rgba(69, 39, 160, 0.06)',
          ],
          borderRadius: 8,
          barThickness: 28,
        },
      ],
    };
  }, [topItems]);

  const topItemsChartOpts = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: 'y',
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundcolor: 'text.primary',
          cornerRadius: 8,
          padding: 10,
        },
      },
      scales: {
        x: {
          grid: { color: 'rgba(0, 0, 0, 0.03)' },
          ticks: { font: { size: 10 }, color: 'text.disabled' },
          beginAtZero: true,
        },
        y: {
          grid: { display: false },
          ticks: { font: { size: 11, weight: '600' }, color: 'text.primary' },
        },
      },
    }),
    []
  );

  // ── Chart: Order Status Doughnut ───────────────────────────────────────
  const orderStatusData = useMemo(() => {
    return {
      labels: ['Active', 'Completed'],
      datasets: [
        {
          data: [activeCount, totalOrders],
          backgroundColor: ['#FF2D55', '#34C759'],
          borderWidth: 0,
          hoverOffset: 6,
        },
      ],
    };
  }, [activeCount, totalOrders]);

  const doughnutOpts = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      cutout: '70%',
      plugins: {
        legend: {
          position: 'bottom',
          labels: { font: { size: 11, weight: '600' }, padding: 16, usePointStyle: true, pointStyleWidth: 8 },
        },
        tooltip: {
          backgroundcolor: 'text.primary',
          cornerRadius: 8,
          padding: 10,
        },
      },
    }),
    []
  );

  // ── Loading state ──────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
        }}
      >
        <CircularProgress size={32} sx={{ color: 'primary.main' }} />
        <Typography sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '0.85rem' }}>
          Loading dashboard…
        </Typography>
      </Box>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        bgcolor: 'background.default',
      }}
    >
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          pt: 2,
          pb: 1,
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <IconButton
            onClick={onBack}
            size="small"
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.72)',
              width: 32,
              height: 32,
              '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.5)' },
            }}
          >
            <ArrowBack sx={{ fontSize: 16 }} />
          </IconButton>
          <Typography
            variant="h6"
            sx={{ fontWeight: 800, fontSize: '1.1rem', color: 'text.primary', letterSpacing: '-0.3px' }}
          >
            Dashboard
          </Typography>
        </Box>
        <IconButton
          onClick={handleRefresh}
          disabled={refreshing}
          size="small"
          sx={{
            bgcolor: 'rgba(255, 255, 255, 0.72)',
            width: 32,
            height: 32,
            '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.5)' },
          }}
        >
          {refreshing ? (
            <CircularProgress size={14} sx={{ color: 'text.secondary' }} />
          ) : (
            <Refresh sx={{ fontSize: 16, color: 'text.secondary' }} />
          )}
        </IconButton>
      </Box>

      {/* ── Date Range Picker ───────────────────────────────────────────── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 3,
          pb: 1.5,
          flexShrink: 0,
          flexWrap: 'wrap',
        }}
      >
        {/* Preset chips */}
        {PRESETS.map((p) => (
          <Chip
            key={p.key}
            label={p.label}
            size="small"
            onClick={() => handlePreset(p)}
            sx={{
              fontWeight: 700,
              fontSize: '0.72rem',
              height: 28,
              borderRadius: '8px',
              bgcolor: activePreset === p.key ? '#4527A0' : 'rgba(255, 255, 255, 0.72)',
              color: activePreset === p.key ? 'rgba(255, 255, 255, 0.72)' : '#1D1D1F',
              boxShadow: activePreset === p.key ? 'none' : '0 1px 3px rgba(0,0,0,0.04)',
              '&:hover': {
                bgcolor: activePreset === p.key ? '#5E35B1' : 'rgba(0, 0, 0, 0.03)',
              },
              transition: 'all 0.15s ease',
            }}
          />
        ))}

        {/* Divider */}
        <Box sx={{ width: 1, height: 20, bgcolor: 'rgba(0, 0, 0, 0.06)', mx: 0.5 }} />

        {/* Date inputs */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <CalendarToday sx={{ fontSize: 14, color: 'text.secondary' }} />
          <TextField
            type="date"
            size="small"
            value={toISODate(rangeStart)}
            onChange={handleCustomStart}
            inputProps={{ max: toISODate(rangeEnd) }}
            sx={{
              width: 130,
              '& .MuiOutlinedInput-root': {
                height: 28,
                borderRadius: '8px',
                bgcolor: 'rgba(255, 255, 255, 0.72)',
                fontSize: '0.75rem',
                fontWeight: 600,
                '& fieldset': { borderColor: 'rgba(0, 0, 0, 0.06)' },
                '&:hover fieldset': { bordercolor: 'text.disabled' },
              },
              '& .MuiOutlinedInput-input': { py: 0.5, px: 1 },
            }}
          />
          <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', fontWeight: 600 }}>to</Typography>
          <TextField
            type="date"
            size="small"
            value={toISODate(rangeEnd)}
            onChange={handleCustomEnd}
            inputProps={{ min: toISODate(rangeStart), max: toISODate(new Date()) }}
            sx={{
              width: 130,
              '& .MuiOutlinedInput-root': {
                height: 28,
                borderRadius: '8px',
                bgcolor: 'rgba(255, 255, 255, 0.72)',
                fontSize: '0.75rem',
                fontWeight: 600,
                '& fieldset': { borderColor: 'rgba(0, 0, 0, 0.06)' },
                '&:hover fieldset': { bordercolor: 'text.disabled' },
              },
              '& .MuiOutlinedInput-input': { py: 0.5, px: 1 },
            }}
          />
        </Box>
      </Box>

      {/* ── Scrollable Content ─────────────────────────────────────────── */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: 3, pb: 3 }}>
        {/* ── KPI Cards ────────────────────────────────────────────────── */}
        <Box sx={{ display: 'flex', gap: 1.5, mb: 2.5, flexWrap: 'wrap' }}>
          <KpiCard
            icon={<AttachMoney sx={{ fontSize: 18 }} />}
            label="Revenue"
            value={fmt(totalRevenue)}
            sub={rangeLabel}
            color="#4527A0"
            bgGrad="linear-gradient(135deg, #4527A0 0%, #7E57C2 100%)"
          />
          <KpiCard
            icon={<Receipt sx={{ fontSize: 18 }} />}
            label="Orders"
            value={totalOrders.toLocaleString('en-IN')}
            sub={`Completed · ${rangeLabel}`}
            color="#FF2D55"
            bgGrad="linear-gradient(135deg, #FF2D55 0%, #FF6B8A 100%)"
          />
          <KpiCard
            icon={<TrendingUp sx={{ fontSize: 18 }} />}
            label="Avg Order Value"
            value={fmt(avgOrder)}
            sub="Per order"
            color="#FF9500"
            bgGrad="linear-gradient(135deg, #FF9500 0%, #FFB84D 100%)"
          />
          <KpiCard
            icon={<ShoppingCart sx={{ fontSize: 18 }} />}
            label="Active Orders"
            value={activeCount.toString()}
            sub="Right now"
            color="#34C759"
            bgGrad="linear-gradient(135deg, #34C759 0%, #5DD27A 100%)"
          />
        </Box>

        {/* ── Charts Row ───────────────────────────────────────────────── */}
        <Box sx={{ display: 'flex', gap: 1.5, mb: 2.5, flexWrap: 'wrap' }}>
          {/* Sales Trend */}
          <Section title={`Revenue Trend — ${rangeLabel}`} sx={{ flex: 2, minWidth: 340 }}>
            <Box sx={{ height: 240, px: 2.5, pb: 2 }}>
              {salesChartData ? (
                <Line data={salesChartData} options={salesChartOpts} />
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <Typography sx={{ color: 'text.disabled', fontSize: '0.82rem' }}>
                    No revenue data for this period
                  </Typography>
                </Box>
              )}
            </Box>
          </Section>

          {/* Order Status Breakdown */}
          <Section title="Order Status" sx={{ flex: 1, minWidth: 200 }}>
            <Box sx={{ height: 240, px: 2, pb: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {activeCount + totalOrders > 0 ? (
                <Doughnut data={orderStatusData} options={doughnutOpts} />
              ) : (
                <Typography sx={{ color: 'text.disabled', fontSize: '0.82rem' }}>
                  No orders yet
                </Typography>
              )}
            </Box>
          </Section>
        </Box>

        {/* ── Second Charts Row ────────────────────────────────────────── */}
        <Box sx={{ display: 'flex', gap: 1.5, mb: 2.5, flexWrap: 'wrap' }}>
          {/* Top Items */}
          <Section title="Top Selling Items (All Time)" sx={{ flex: 1, minWidth: 320 }}>
            <Box sx={{ height: 220, px: 2.5, pb: 2 }}>
              {topItems.length > 0 ? (
                <Bar data={topItemsChartData} options={topItemsChartOpts} />
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <Typography sx={{ color: 'text.disabled', fontSize: '0.82rem' }}>
                    No item data available
                  </Typography>
                </Box>
              )}
            </Box>
          </Section>

          {/* Top Items as ranked list */}
          <Section title="Best Sellers (All Time)" sx={{ flex: 1, minWidth: 280 }}>
            <Box sx={{ px: 2.5, pb: 2 }}>
              {topItems.length === 0 ? (
                <Typography sx={{ color: 'text.disabled', fontSize: '0.82rem', py: 4, textAlign: 'center' }}>
                  No data
                </Typography>
              ) : (
                topItems.slice(0, 5).map((item, idx) => (
                  <Box
                    key={item.item_name || idx}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      py: 1.25,
                      borderBottom: idx < Math.min(topItems.length, 5) - 1 ? '1px solid rgba(0, 0, 0, 0.03)' : 'none',
                      gap: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '8px',
                        bgcolor: idx === 0 ? '#FFF3E0' : idx === 1 ? '#F3E5F5' : idx === 2 ? 'rgba(52, 199, 89, 0.08)' : '#F5F5F5',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      {idx === 0 ? (
                        <EmojiEvents sx={{ fontSize: 15, color: '#FF9500' }} />
                      ) : (
                        <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: 'text.secondary' }}>
                          {idx + 1}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography noWrap sx={{ fontSize: '0.82rem', fontWeight: 600, color: 'text.primary' }}>
                        {item.item_name}
                      </Typography>
                      <Typography sx={{ fontSize: '0.68rem', color: 'text.disabled' }}>
                        {item.total_quantity} sold
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: 'primary.main', flexShrink: 0 }}>
                      {fmt(item.total_revenue || item.total_amount || 0)}
                    </Typography>
                  </Box>
                ))
              )}
            </Box>
          </Section>
        </Box>

        {/* ── Data Tables ──────────────────────────────────────────────── */}
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          {/* Recent Completed Orders */}
          <Section title={`Completed Orders — ${rangeLabel}`} sx={{ flex: 1, minWidth: 380 }}>
            <TableContainer sx={{ maxHeight: 320 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {['#', 'Items', 'Amount', 'Date'].map((h) => (
                      <TableCell
                        key={h}
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.72rem',
                          color: 'text.secondary',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          bgcolor: 'background.default',
                          borderBottom: '1px solid rgba(0, 0, 0, 0.03)',
                          py: 1.25,
                        }}
                      >
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {completedOrders.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.disabled', fontSize: '0.82rem' }}>
                        No completed orders in this period
                      </TableCell>
                    </TableRow>
                  ) : (
                    completedOrders.slice(0, 15).map((order, idx) => (
                      <TableRow key={order.OrderID || idx} hover sx={{ '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.5)' } }}>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.82rem', color: 'text.primary', py: 1.25 }}>
                          {order.OrderNum || '—'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.78rem', color: '#1D1D1F', py: 1.25, maxWidth: 200 }}>
                          <Typography noWrap sx={{ fontSize: 'inherit' }}>
                            {(order.ItemNames || []).join(', ') || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.82rem', color: 'primary.main', py: 1.25 }}>
                          ₹{(order.Amount || 0).toLocaleString('en-IN')}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.72rem', color: 'text.disabled', py: 1.25, whiteSpace: 'nowrap' }}>
                          {formatFullDate(order.CreatedAt)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Section>

          {/* Daily Ledger Summaries */}
          <Section title={`Daily Sales — ${rangeLabel}`} sx={{ flex: 1, minWidth: 380 }}>
            <TableContainer sx={{ maxHeight: 320 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {['Date', 'Sales', 'Orders'].map((h) => (
                      <TableCell
                        key={h}
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.72rem',
                          color: 'text.secondary',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          bgcolor: 'background.default',
                          borderBottom: '1px solid rgba(0, 0, 0, 0.03)',
                          py: 1.25,
                        }}
                      >
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ledgerEntries.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} align="center" sx={{ py: 4, color: 'text.disabled', fontSize: '0.82rem' }}>
                        No ledger entries
                      </TableCell>
                    </TableRow>
                  ) : (
                    ledgerEntries
                      .slice()
                      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                      .slice(0, 20)
                      .map((entry, idx) => (
                        <TableRow key={entry.ledger_entry_id || idx} hover sx={{ '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.5)' } }}>
                          <TableCell sx={{ fontSize: '0.78rem', color: '#1D1D1F', py: 1.25, whiteSpace: 'nowrap' }}>
                            {shortDate(entry.date)}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 700,
                              fontSize: '0.82rem',
                              color: (entry.total_sales || 0) > 0 ? '#34C759' : '#AEAEB2',
                              py: 1.25,
                            }}
                          >
                            ₹{(entry.total_sales || 0).toLocaleString('en-IN')}
                          </TableCell>
                          <TableCell sx={{ py: 1.25 }}>
                            <Chip
                              label={`${entry.number_of_orders || 0} orders`}
                              size="small"
                              sx={{
                                fontWeight: 700,
                                fontSize: '0.62rem',
                                height: 22,
                                bgcolor: (entry.number_of_orders || 0) > 0 ? 'rgba(52, 199, 89, 0.08)' : '#F5F5F5',
                                color: (entry.number_of_orders || 0) > 0 ? '#2E7D32' : '#86868B',
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Section>
        </Box>
      </Box>
    </Box>
  );
};
